import React, { useEffect } from "react";
import { FormTextInput } from "components/form/form-input/form-text-input";
import { InputTypes } from "components/form/enumerations/input-types";
import { AenProviderFeeRecord } from "models/view-models/aen-resources/aen-provider-fee-record";
import { FormCalendarDatePicker } from "components/form/form-calendar-date-picker/form-calendar-date-picker";
import { FormFieldReadonly } from "components/form/form-field-readonly/form-field-readonly";
import { toast } from "react-toastify";
import { t } from "utilities/localization/t";
import "./aen-provider-fee-form.scss";
// -------------------------------------------------------------------------------------------------
// #region Interfaces
// -------------------------------------------------------------------------------------------------

interface AenProviderFeeFormProps {
    aenProviderFee: AenProviderFeeRecord;
    setAenProviderFee: React.Dispatch<React.SetStateAction<AenProviderFeeRecord>>;
    setDirty: React.Dispatch<React.SetStateAction<boolean>>;
}

// #endregion Interfaces

// -------------------------------------------------------------------------------------------------
// #region Constants
// ------------------------------------------------------------------------------------------------

const CSS_CLASS_NAME: string = "aen-provider-fee-form";

// #endregion Constants

// -------------------------------------------------------------------------------------------------
// #region Component
// -------------------------------------------------------------------------------------------------

const AenProviderFeeForm: React.FC<AenProviderFeeFormProps> = (props) => {
    const [deadline, setDeadline] = React.useState(props.aenProviderFee.deadline);
    
    const updateAenProviderFee = (value: Partial<AenProviderFeeRecord>): void => {
        props.setAenProviderFee((prev) => prev.with(value));
        props.setDirty(true);
    };

    const [amountHasChanged, setAmountHasChanged] = React.useState(false);
    const [amountInput, setAmountInput] = React.useState(props.aenProviderFee?.amount?.toFixed(2) ?? "");

    useEffect(() => {
        if (props.aenProviderFee.id != null) {
            const formattedAmount = amountInput === "" ? "" : parseFloat(amountInput).toFixed(2);
            setAmountInput(formattedAmount);
            if (amountHasChanged) {
                updateAenProviderFee({ amount: formattedAmount === "" ? undefined : parseFloat(formattedAmount) });
            }   
        }
    }, [props.aenProviderFee?.amount]);
    
    const handleAmountOnBlur = () => {
        //remove any non-numeric characters from the input before parsing
        const numericInput = amountInput.replace(/[^0-9.]/g, "");
        if (numericInput.length !== amountInput.length) {
            toast.error(t("invalidCharactersRemovedFromInput"));
        }
        const value = parseFloat(numericInput);
        
        if (Number.isNaN(value)) {
            setAmountInput("");
            updateAenProviderFee({ amount: undefined });
            return;
        }
        updateAenProviderFee({ amount: value });
        setAmountInput(value.toFixed(2));
    };

    return (
        <React.Fragment>
            <div className={CSS_CLASS_NAME}>
                <FormFieldReadonly
                    label={t("year")}
                >{props.aenProviderFee.year}</FormFieldReadonly>
                <FormTextInput
                    formFieldName={t("amount")}
                    id="amount"
                    label={t("amount")}
                    maxLength={20}
                    onChange={(e) => {
                        setAmountInput(e.target.value);
                        setAmountHasChanged(true);
                    }}
                    placeholder={t("enterProviderFeeAmount")}
                    required={true}
                    type={InputTypes.Text}
                    value={amountInput}
                    onBlur={handleAmountOnBlur}
                />

                <FormCalendarDatePicker
                    cssClassName={`${CSS_CLASS_NAME}__event-day-date`}
                    formFieldName={t("deadline")}
                    label={t("deadline")}
                    required={true}
                    selectedDate={deadline}
                    onChange={(date: any) => {
                        setDeadline(date);
                        updateAenProviderFee({ deadline: date ?? undefined });
                    }}
                    minDate={new Date(props.aenProviderFee.year, 0, 1)}
                />
                           
            </div>
        </React.Fragment>
    );
};

// #endregion Component

// -------------------------------------------------------------------------------------------------
// #region Exports
// -------------------------------------------------------------------------------------------------

export { AenProviderFeeForm };

// #endregion Exports
